// tronweb (lazy-loaded for Tron signing) expects Node's global Buffer, which
// Vite doesn't provide in the browser. Polyfill it before anything else runs.
import { Buffer } from 'buffer';
const glob = globalThis as unknown as { Buffer?: typeof Buffer };
if (!glob.Buffer) glob.Buffer = Buffer;

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { App } from './App';
import { ApiError } from './lib/api';
import { setToken } from './lib/session';
import { useOnboarding } from './store/onboarding';
import './i18n';
import './index.css';

// A session that was terminated / expired on another device → log this device
// out: drop the token, clear caches, and return to onboarding (the authed tree
// unmounts, so nothing re-bootstraps until the user onboards again).
const REVOKED = new Set(['session_revoked', 'session_expired', 'invalid_token']);
function onAuthError(err: unknown) {
  if (err instanceof ApiError && err.status === 401 && REVOKED.has(err.code)) {
    setToken(null);
    queryClient.clear();
    useOnboarding.getState().reset();
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onAuthError }),
  mutationCache: new MutationCache({ onError: onAuthError }),
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
